import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import cloneDeep from 'lodash-es/cloneDeep';
import get from 'lodash-es/get';
import { useCallback, useMemo } from 'react';

import type { AlgoliaFacets, FilterSelectItem } from '@jane/search/types';
import { formatActiveFilters, handleFilterChange } from '@jane/search/util';
import { useGetStore } from '@jane/shared/data-access';
import type { Store, StoreSpecial } from '@jane/shared/types';

import type { RootLoaderType } from '../../../data-access';
import { useTypedRouteLoaderData } from '../../../hooks';
import {
  useComplianceLanguage,
  useMenu,
  useSearchContext,
} from '../../../providers';
import { capitalize, hasFilterableOptions } from '../../../util';
import { QuickFilterButton } from '../QuickFilterButton/QuickFilterButton';

interface SpecialQuickFilterProps {
  filteredProductCount: number;
  presentedFacets?: AlgoliaFacets;
  supportedStoreSpecials: (StoreSpecial | V3StoreSpecial)[];
  totalProductCount: number;
}

const specialFilterSelections = (
  specialIds: string[],
  storeSpecials: (StoreSpecial | V3StoreSpecial)[],
  facets: AlgoliaFacets
): FilterSelectItem[] => {
  return specialIds.reduce((acc: FilterSelectItem[], specialId) => {
    const matchingSpecial = storeSpecials.find(
      ({ id }) => id.toString() === specialId
    );
    if (!matchingSpecial) {
      return acc;
    } else {
      return [
        ...acc,
        {
          count: get(facets, ['applicable_special_ids', specialId]) ?? 0,
          label: matchingSpecial.title,
          value: specialId,
        },
      ];
    }
  }, []);
};

export const SpecialQuickFilter = ({
  filteredProductCount,
  presentedFacets,
  supportedStoreSpecials,
  totalProductCount,
}: SpecialQuickFilterProps) => {
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { filterableSpecialIds } = useMenu();
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const { resetSearchState, searchState, setSearchState } = useSearchContext();
  const { getWord } = useComplianceLanguage();

  const specialFilterItems = useMemo(
    () =>
      specialFilterSelections(
        filterableSpecialIds,
        supportedStoreSpecials,
        presentedFacets ?? {}
      ),
    [filterableSpecialIds, supportedStoreSpecials, presentedFacets]
  );

  const onClearAllQuickFilters = useCallback(() => {
    const newState = cloneDeep(searchState);
    if (newState.filters?.applicable_special_ids) {
      newState.filters.applicable_special_ids = [];
    }

    resetSearchState(newState);
  }, [resetSearchState, searchState]);

  const onSpecialSelected = useCallback(
    (option: FilterSelectItem) => {
      return setSearchState(
        handleFilterChange('applicable_special_ids', option.value, searchState)
      );
    },
    [searchState, setSearchState]
  );

  const activeSpecialFilters = formatActiveFilters(searchState, {
    specials: supportedStoreSpecials,
    storeId: store?.id,
  }).filter(({ key }) => key === 'applicable_special_ids');

  if (!hasFilterableOptions(specialFilterItems)) {
    return null;
  }

  return (
    <QuickFilterButton
      activeFilters={activeSpecialFilters}
      label={capitalize(getWord('deal') ?? 'Deal')}
      onClearAll={onClearAllQuickFilters}
      onOptionSelect={onSpecialSelected}
      options={specialFilterItems}
      filteredProductCount={filteredProductCount}
      totalProductCount={totalProductCount}
      store={store}
    />
  );
};
